// Libraries
import { ActionMeta, GroupBase, MultiValue, OnChangeValue, Select, SingleValue } from 'chakra-react-select';
import { useCallback, useEffect, useState } from 'react';
// Components
import { selectComponents } from './FixedDropdown.components';
import { selectStyles } from './FixedDropdown.styles';
// Utils
import text from '@translations/en.json';
// Types
import { BasicOption, SelectKeys, WrapperProps } from './FixedDropdown.type';

/**
 * Wrapper component for a Select component with additional functionality.
 *
 * @component
 * @param {WrapperProps<OptionType, IsMulti>} props - The props for the Wrapper component.
 * @param {string} props.id - The ID of the Select component.
 * @param {string} props.name - Optional. The name attribute of the Select component.
 * @param {boolean} props.isMulti - Indicates if the Select component allows multiple selections.
 * @param {string} props.noOptionsMessage - Optional. The message to display when no options are available.
 * @param {OptionType[]} props.options - The available options for the Select component.
 * @param {OptionType[]} props.value - The currently selected value or values from the dropdown. For multi-select, it's an array.
 * @param {OptionType[]} props.placeholder - The message to display in the search filed for the filter.
 * @param {Function} props.onChange - The callback function triggered when the selected options change.
 * @returns {ReactElement} The JSX element representing the Wrapper component.
 * @template {BasicOption} OptionType - The type of the options in the Select component.
 * @template {boolean} IsMulti - A boolean value indicating if the Select component allows multiple selections.
 */
export const Wrapper = <OptionType extends BasicOption, IsMulti extends boolean>({
  id,
  name,
  isMulti,
  noOptionsMessage,
  options,
  value = null,
  placeholder = text.search,
  onChange,
}: WrapperProps<OptionType, IsMulti>) => {
  const [selectedOptions, setSelectedOptions] = useState<
    (IsMulti extends true ? MultiValue<OptionType> : SingleValue<OptionType>) | null
  >(value);

  /**
   * Event handler function triggered when the selected options change in the Select component.
   *
   * @param {OnChangeValue<OptionType, IsMulti>} selected - The selected options.
   * @param {ActionMeta<OptionType>} actionMeta - Additional metadata about the action.
   */
  const handleOnChange = (newOptions: OnChangeValue<OptionType, IsMulti>, actionMeta: ActionMeta<OptionType>) => {
    if (actionMeta.option?.value === SelectKeys.SELECT_ALL) {
      if ((newOptions as OptionType[]).length === options.length) {
        onChange?.([]);
      } else {
        const optionsSend = options.filter(({ value }) => value !== SelectKeys.SELECT_ALL);
        onChange?.(optionsSend);
      }
    } else {
      onChange?.(newOptions as OptionType[]);
    }
  };

  /**
   * Effect used to allow the parent to control the state.
   *
   * @effect
   */
  useEffect(() => {
    setSelectedOptions(value);
  }, [value]);

  /**
   * Callback function used to create the "no options" message for the Select component.
   *
   * @param {object} obj - An object containing the input value.
   * @param {string} obj.inputValue - The input value entered by the user.
   * @returns {string} The "no options" message for the Select component.
   */
  const createNoOptionsMessage = useCallback(
    (obj: { inputValue: string }) => `${noOptionsMessage} ${obj.inputValue}`,
    [noOptionsMessage]
  );

  return (
    <Select<OptionType, IsMulti, GroupBase<OptionType>>
      id={id}
      name={name}
      closeMenuOnSelect={false}
      isMulti={isMulti}
      options={options}
      value={selectedOptions}
      chakraStyles={{ ...selectStyles() }}
      components={{ ...selectComponents() }}
      menuIsOpen={true}
      hideSelectedOptions={false}
      backspaceRemovesValue={false}
      noOptionsMessage={createNoOptionsMessage}
      placeholder={placeholder}
      classNamePrefix='react-select'
      isClearable={false}
      onChange={handleOnChange}
      tabSelectsValue={false}
    />
  );
};
